// reference: https://prismic.io/docs/rest-api-technical-reference
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { statistics } from '../../../../assets/static/stats';
import { LearnGroup } from '../../enum/learn-group.enum';
import { Article } from '../../models/learn/article';
import { SurveyFormFields } from '../../models/survey-form/form-fields';

@Injectable()
export class PrismicService {
  private articles = Object.keys(LearnGroup).reduce((acc, key) => ({ ...acc, [key]: null }), {});
  private subMenus = Object.keys(LearnGroup).reduce((acc, key) => ({ ...acc, [key]: null }), {});
  private tableOfContents: object;

  // Learn/Articles still flow through the API's Prismic proxy (handled separately by the KMP
  // migration, issue #39). The homepage statistics + investment surveys no longer touch Prismic at
  // all — they are served from owned config via this same API route (see methods below, #36).
  private route = environment.apiServer + 'Api/v1/StaticUI/public';

  constructor(private http: HttpClient) {}

  private getEnumKey(enumValue) {
    return Object.keys(LearnGroup).find((k) => LearnGroup[k] == enumValue);
  }

  getHomepage(): Observable<object> {
    const params = { noToken: 'noToken' };

    if (this.tableOfContents) {
      return of(this.tableOfContents);
    }

    return this.http.get(this.route + '/homepage', { params: params }).pipe(
      map((data: any) => {
        this.tableOfContents = data.result;

        return data.result;
      }),
    );
  }

  getSubmenu(group: LearnGroup): Observable<Article[]> {
    const params = { noToken: 'noToken', group: group };
    const key = this.getEnumKey(group);

    if (key && this.subMenus[key]) return of(this.subMenus[key]);

    return this.http.get(this.route + '/submenu', { params: params }).pipe(
      map((data: any) => {
        this.subMenus[key] = data.result;

        return data.result;
      }),
    );
  }

  getArticle(slug: string, group?: LearnGroup): Observable<Article> {
    const params = { noToken: 'noToken', group: group };

    return this.http.get(this.route + '/article/' + slug, { params: params }).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  sortArticle(list: Article[]) {
    const customSort = (a, b) => {
      if (a.data.position == null) return 1;
      if (b.data.position == null) return -1;
      return a.data.position - b.data.position;
    };

    return list.sort(customSort);
  }

  // #36: homepage statistics + the investment surveys moved OFF Prismic onto our own API
  // (config-backed) so the Prismic subscription can be cancelled without breaking the homepage.
  // Response shapes are identical to the old Prismic document `.data`, so callers/templates are
  // unchanged. The statistics are public financial claims served verbatim from owned config — they
  // are NOT a live money/settlement query.
  getFrontPageStats(): Observable<any> {
    const params = { noToken: 'noToken' };

    return this.http.get(this.route + '/homepage-statistics', { params }).pipe(
      catchError(() =>
        of({
          total_raised: statistics.raised,
          number_of_investors: statistics.investors,
          number_of_campaigns: statistics.campaigns,
        }),
      ),
    );
  }

  getCancelInvestmentSurvey(): Observable<SurveyFormFields> {
    const params = { noToken: 'noToken' };

    return this.http.get<SurveyFormFields>(this.route + '/survey/cancel-investment', { params });
  }

  getRefundInvestmentSurvey(): Observable<SurveyFormFields> {
    const params = { noToken: 'noToken' };

    return this.http.get<SurveyFormFields>(this.route + '/survey/refund-investment', { params });
  }
}
